// Models
import {Signal} from "../records/models"

/**
 * The record's sample rate, from its own signals — never assumed.
 *
 * Positions are stored in samples and shown in ms, so every conversion
 * depends on this one number. It used to default to 500 while the signals
 * were still loading: labels were converted at 500, latched, and when the real
 * rate arrived (250, say) the next save converted them back at 250 — halving
 * every mark with nobody touching it.
 *
 * So there is no default. Until every lead reports the same finite, positive
 * rate, the answer is "not known", and nothing is converted, shown for
 * editing, or saved.
 */
export type RecordRate =
    | {rate: number, problem: null}
    | {rate: null, problem: "loading" | "missing" | "mixed"}

/**
 * @param signals the record's leads, as loaded (empty while loading)
 */
export function recordSampleRate(signals: Signal[] | null | undefined): RecordRate {
    if (!signals || !signals.length) {
        return {rate: null, problem: "loading"}
    }

    /* typed as a number, but the server sends null for a record without one */
    const rates = signals.map(signal => signal.sampleRate as number | null | undefined);

    if (rates.some(rate => rate === null || rate === undefined || !isFinite(rate) || rate <= 0)) {
        return {rate: null, problem: "missing"}
    }

    if (rates.some(rate => rate !== rates[0])) {
        return {rate: null, problem: "mixed"}
    }

    return {rate: rates[0] as number, problem: null}
}

/**
 * The time of the record's last sample, in ms, or undefined when not known.
 *
 * @param signals
 * @param rate
 */
export function recordEndMs(signals: Signal[] | null | undefined, rate: number | null): number | undefined {
    if (!signals || !signals.length || !rate) {
        return undefined
    }

    const samples = Math.min.apply(null, signals.map(signal => signal.data.length));

    return samples > 0 ? ((samples - 1) * 1000) / rate : undefined
}
